import React, { useMemo } from 'react';
import { Title, Spacer, Seperator } from 'components/atoms';
import styled, { css } from 'styled-components/macro';
import { useRouterContext } from 'hooks';
import { useEditorContext, actions } from 'context/EditorContext';
import { produce } from 'immer'
import { useDispatch } from 'react-redux'
import { saveDraft } from 'redux/draftsSlice'
import SeoEditor from 'modules/Seo/SeoEditor'
import { PageUpdateSlugForm, PageDangerZone } from 'containers'
import PageDuplicate from 'containers/PageDuplicate/PageDuplicate';

const EditorSettings = () => {
  const {
    match: {
      params: {
        lang,
        pageId,
        subweb,
      },
    },
  } = useRouterContext();
  const [{ pageLiveData, pageSlug }, dispatch] = useEditorContext();
  const dispatchRedux = useDispatch()

  // get module props from page data
  const seoProps = useMemo(
    () => pageLiveData.find(({ name }) => name === 'SEO'),
    [pageLiveData]
  );

  const handleOnChange = newProps => {
    // find index of current module being editing
    const moduleIndex = pageLiveData.findIndex(({ name }) => name === 'SEO');

    // update title on first article
    const articleIndex = pageLiveData.findIndex(({ name }) => name === 'Article');
    const articleProps = articleIndex !== -1 ? pageLiveData[articleIndex] : null
    const articlePropsUpdatedTitle = produce(articleProps, draftState => {
      // eslint-disable-next-line no-param-reassign
      if (articleProps && draftState.data) draftState.data.title = newProps.title
    })

    // produce used to create a new immutable page
    const newLiveData = produce(pageLiveData, draftState => {
      // eslint-disable-next-line no-param-reassign
      draftState[moduleIndex] = newProps
      // eslint-disable-next-line no-param-reassign
      if (articleIndex !== -1) draftState[articleIndex] = articlePropsUpdatedTitle
    })

    // verify if the new data is different before dispatching to context
    const pageLiveDataJSON = JSON.stringify(pageLiveData);
    const newLiveDataJSON = JSON.stringify(newLiveData);

    if (pageLiveDataJSON !== newLiveDataJSON) {
      // update editor context
      dispatch({
        type: actions.PAGE_DATA_UPDATE,
        data: newLiveData,
      });
      dispatchRedux(saveDraft({ pageId, content: newLiveData, lang }))
    }
  };

  const initialSlug = useMemo(() => pageSlug || '', [pageSlug])

  return (
    <Wrapper>
      <Top>
        <Title modifiers={['fixMarginBottom']}>Página</Title>
      </Top>
      <Main>
        <SeoEditor initialProps={seoProps} onChange={handleOnChange} />
        <Spacer top="l" bottom="l">
          <Seperator />
        </Spacer>
        <PageUpdateSlugForm initialSlug={initialSlug} pageId={pageId} subweb={subweb} lang={lang} />
        <Spacer top="l" bottom="l">
          <Seperator />
        </Spacer>
        <PageDuplicate pageId={pageId} subweb={subweb} pageLiveData={pageLiveData.filter(data => data.name !== 'SEO')} />
        <Spacer top="l" bottom="l">
          <Seperator />
        </Spacer>
        <PageDangerZone pageId={pageId} subweb={subweb} />
      </Main>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-height: calc(100vh - 90px);
  overflow-y: auto;
`

const Top = styled.div(props => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props.theme.spacing.xl} ${props.theme.spacing.xl} ${props.theme.spacing.m} ${props.theme.spacing.xl3};
`);

const Main = styled.div(props => css`
  padding: 0 ${props.theme.spacing.xl} ${props.theme.spacing.xl3} ${props.theme.spacing.xl3};
`)

export default EditorSettings;
